import { labelModes } from '@/utils'

/**
 * Внешний вид выпадающего списка: подпись, подсказки, режим
 * отображения, значки по краям поля и признаки плотности,
 * скругления и плоского оформления.
 */
export const appearance = {
  props: {
    /**
     * Подпись поля. Выводится над полем и добавляет корневому
     * элементу признак наличия подписи.
     *
     * @type {string}
     */
    label: String,
    /**
     * Подсказка внутри поля ввода. Выводится, когда у компонента
     * есть свое поле ввода.
     *
     * @type {string}
     */
    placeholder: String,

    /**
     * Пояснение под полем. Выводится слева в строке пояснений,
     * пока нет ошибки.
     *
     * @type {string}
     */
    hint: String,

    /**
     * Класс подсветки при фокусе. Применяется к подписи, границе и
     * значкам по краям поля.
     *
     * @type {string}
     * @default 'text--primary'
     */
    focusedClass: {
      type: String,
      default: 'text--primary'
    },

    /**
     * Режим отображения поля. Задает модификатор класса корневого
     * элемента. В режиме 'hidden' подпись скрывается, когда
     * есть строка поиска или выбранные значения.
     *
     * @type {labelModes}
     * @default labelModes.line
     */
    labelMode: {
      type: String,
      default: labelModes.line,
      validator: value => {
        return Object.values(labelModes).includes(value)
      }
    },

    /**
     * Цвет фона основной части поля.
     *
     * @type {string}
     * @default null
     */
    backgroundColor: {
      type: String,
      default: null
    },

    /**
     * Позиция в последовательности табуляции. Передается в поле
     * ввода, а при его отсутствии - в корневой элемент.
     *
     * @type {string|number}
     */
    tabindex: [ String, Number ],

    /**
     * Признак вывода строки пояснений под полем.
     *
     * @type {boolean}
     * @default true
     */
    details: {
      type: Boolean,
      default: true
    },
    /**
     * Признак плоского оформления. Задает модификатор класса
     * корневого элемента.
     *
     * @type {boolean}
     * @default false
     */
    flat: {
      type: Boolean,
      default: false
    },
    /**
     * Признак уплотненного оформления. Задает модификатор класса
     * корневого элемента, уплотняет список элементов и метки
     * выбранных значений.
     *
     * @type {boolean}
     * @default false
     */
    dense: {
      type: Boolean,
      default: false
    },

    /**
     * Значок перед содержимым поля. Выводится в области перед
     * полем, когда нет слота before.
     *
     * @type {string}
     */
    beforeIcon: String,
    /**
     * Обработчик нажатия на значок beforeIcon. Вызывается, если поле
     * не заблокировано. По умолчанию отдает переданный аргумент без
     * изменений.
     *
     * @type {Function}
     */
    beforeIconCallback: {
      type: Function,
      default: v => {
        return v
      }
    },

    /**
     * Значок после содержимого поля. Выводится в области после
     * поля, когда нет слота after и не выводится значок очистки.
     *
     * @type {string}
     * @default 'arrow_drop_down'
     */
    afterIcon: {
      type: String,
      default: 'arrow_drop_down'
    },
    /**
     * Обработчик нажатия на значок afterIcon. Вызывается, если поле
     * не заблокировано. По умолчанию отдает переданный аргумент без
     * изменений.
     *
     * @type {Function}
     */
    afterIconCallback: {
      type: Function,
      default: v => {
        return v
      }
    },

    /**
     * Признак скрытия выбранных значений при фокусе. Действует,
     * когда у компонента есть свое поле ввода.
     *
     * @type {boolean}
     * @default false
     */
    hideSelections: {
      type: Boolean,
      default: false
    },

    /**
     * Счетчик, выводимый справа в строке пояснений.
     *
     * @type {string|number}
     */
    count: [ String, Number ]
  },

  computed: {
    /**
     * Отдает значение tabindex для корневого элемента.
     * Если isDisabled или hasInput, отдается undefined: заблокированное
     * поле фокус не принимает, а при наличии своего поля ввода фокус
     * принимает оно, а корень остается вне последовательности
     * табуляции. Если задано свойство tabindex, отдается его
     * значение, иначе отдается 0.
     *
     * @returns {string|number|undefined} Значение атрибута tabindex.
     */
    $_tabindex() {
      if (this.isDisabled || this.hasInput) {
        return undefined
      }
      if (this.tabindex !== undefined) {
        return this.tabindex
      }

      return 0
    },

    /**
     * Отдает признак наличия области перед полем.
     * Область выводится, если есть слот before или задан beforeIcon.
     *
     * @returns {boolean|string|undefined} Признак наличия области.
     */
    hasBefore() {
      return this.hasBeforeSlot || this.beforeIcon
    },
    /**
     * Отдает признак наличия слота before.
     *
     * @returns {boolean} Признак наличия слота.
     */
    hasBeforeSlot() {
      return !!(this.$scopedSlots.before || this.$slots.before)
    },

    /**
     * Отдает признак наличия области после поля.
     * Область выводится, если есть слот after, или loading,
     * или isClearable, или задан afterIcon.
     *
     * @returns {boolean|string|undefined} Признак наличия области.
     */
    hasAfter() {
      return this.hasAfterSlot || this.loading || this.isClearable || this.afterIcon
    },
    /**
     * Отдает признак наличия слота after.
     *
     * @returns {boolean} Признак наличия слота.
     */
    hasAfterSlot() {
      return !!(this.$scopedSlots.after || this.$slots.after)
    },

    /**
     * Отдает признак вывода подписи поля.
     * Если нет label, отдается false. Если labelMode равен 'hidden'
     * и есть строка поиска или выбранные значения,
     * отдается false. В остальных случаях отдается true.
     *
     * @returns {boolean} Признак вывода подписи.
     */
    hasLabel() {
      if (!this.label) {
        return false
      }

      switch (this.labelMode) {
        case labelModes.hidden: return !this.search && !this.proxy.length
        default: return true
      }
    }
  }
}
